"use client";

import { ErrorMessage } from "@/shared/components/common/ErrorMessage";
import * as Sentry from "@sentry/nextjs";
import { useEffect } from "react";

/**
 * GLOBAL ERROR BOUNDARY
 *
 * Purpose: Catches errors that occur in the ROOT layout.tsx file itself
 *
 * When this triggers:
 * - Theme generation fails in layout.tsx
 * - Provider setup crashes (IntercomProvider, ClientLayout, etc.)
 * - Root-level component initialization errors
 * - Errors in <html> or <body> tags
 *
 * Why it's needed:
 * - error.tsx lives INSIDE the layout, so if layout crashes, error.tsx can't render
 * - This wraps the ENTIRE app including the layout
 * - Must include <html> and <body> tags because it replaces the whole page
 */

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
}) {
  useEffect(() => {
    Sentry.captureException(error, {
      tags: {
        errorType: "system",
        location: "global-boundary",
      },
    });
  }, [error]);

  const handleLogout = (): void => {
    window.location.href = "/auth";
  };

  return (
    <html>
      <body>
        <div className="flex min-h-screen items-center justify-center p-4 bg-slate-50">
          <ErrorMessage
            title="Application Error"
            message="An unexpected error occurred. Please logout and try again."
            handleAction={handleLogout}
            buttonText="Logout"
          />
        </div>
      </body>
    </html>
  );
}
